import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useChatScroll } from "../../hooks/useChatAutoScroll";
import { ChatTypingContext } from "./ChatTypingContext";
import styles from "./ChatTurnLayout.module.scss";

interface ChatTurnLayoutProps {
  // Messages of the current step, continuing the thread.
  thread: ReactNode;
  // Quick replies and the composer, pinned above the keyboard.
  actions: ReactNode;
  // Changes whenever a message is added, bringing the newest one into view.
  messageCount?: number;
}

export function ChatTurnLayout({
  thread,
  actions,
  messageCount = 0,
}: ChatTurnLayoutProps) {
  const scroll = useChatScroll();
  const [queue, setQueue] = useState<string[]>([]);

  const enqueue = useCallback(
    (id: string) =>
      setQueue((current) =>
        current.includes(id) ? current : [...current, id],
      ),
    [],
  );
  const dequeue = useCallback(
    (id: string) =>
      setQueue((current) => current.filter((item) => item !== id)),
    [],
  );

  const typing = useMemo(
    () => ({ queue, enqueue, dequeue }),
    [queue, enqueue, dequeue],
  );

  // A new message brings the end back, even after the user scrolled up; the
  // chat follows what it then types by itself.
  useEffect(() => {
    scroll?.scrollToEnd();
  }, [messageCount, scroll]);

  return (
    <ChatTypingContext.Provider value={typing}>
      <div className={styles.thread}>{thread}</div>
      <div className={styles.actions}>{actions}</div>
    </ChatTypingContext.Provider>
  );
}
